import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError, errorMessage } from '../lib/api';
import { useDocumentTitle } from '../lib/hooks';
import type { Project, Session } from '../lib/types';
import { useAuth } from '../state/auth';
import { Field, PageLoading } from '../components/ui';

type Mode = 'account' | 'project';

interface GateInfo {
  gateEnabled: boolean;
  name?: string;
  clientName?: string | null;
  username?: string;
}

function AuthFrame({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">
          <div className="brand" role="img" aria-label="Revisions by MK-Way">
            <span className="brand-mark" aria-hidden="true">
              R
            </span>
            <span className="brand-word" style={{ fontSize: 28 }}>
              Revisions
            </span>
            <span className="brand-by" aria-hidden="true">
              <span className="brand-by-text">by</span>
              <img className="brand-logo" src="/mk-way-logo.png" alt="" width="288" height="154" />
            </span>
          </div>
          <div>
            <h1 style={{ fontSize: 'var(--fs-xl)' }}>{title}</h1>
            <p className="muted" style={{ marginTop: 4 }}>
              {subtitle}
            </p>
          </div>
        </div>
        {children}
        <p className="muted" style={{ fontSize: 'var(--fs-xs)' }}>
          Revisions is where MK-Way and its clients review websites, creative and video. Click anything and say what should change.
        </p>
      </div>
    </div>
  );
}

function AccountForm({ onDone }: { onDone: (session: Session) => void }) {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onDone(await api.post<Session>('/api/auth/login', { identifier, password }));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="auth-form" onSubmit={submit}>
      <Field label="Email or username">
        {(id) => <input id={id} className="input" autoComplete="username" value={identifier} onChange={(e) => setIdentifier(e.target.value)} required autoFocus />}
      </Field>
      <Field label="Password">
        {(id) => <input id={id} className="input" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />}
      </Field>
      {error && (
        <div className="notice notice-error" role="alert">
          {error}
        </div>
      )}
      <button className="btn btn-primary btn-lg btn-block" disabled={busy}>
        {busy && <span className="spinner" />}
        Sign in
      </button>
    </form>
  );
}

function ProjectForm({ project, locked, onDone }: { project: string; locked: boolean; onDone: (session: Session & { projectId?: string }) => void }) {
  const [username, setUsername] = useState(project);
  const [password, setPassword] = useState('');
  const [name, setName] = useState(() => localStorage.getItem('revisions.guestName') || '');
  const [email, setEmail] = useState(() => localStorage.getItem('revisions.guestEmail') || '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const session = await api.post<Session & { projectId?: string }>('/api/auth/project-login', { project: username, password, name, email });
      // Remember who they are (never the password) so returning is one field.
      localStorage.setItem('revisions.guestName', name);
      localStorage.setItem('revisions.guestEmail', email);
      onDone(session);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="auth-form" onSubmit={submit}>
      {!locked && (
        <Field label="Project username" hint="MK-Way sent this with your review link.">
          {(id) => <input id={id} className="input" autoCapitalize="none" value={username} onChange={(e) => setUsername(e.target.value)} required autoFocus />}
        </Field>
      )}
      <Field label="Project password">
        {(id) => <input id={id} className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus={locked} />}
      </Field>
      <Field label="Your name" hint="Shown next to your comments.">
        {(id) => <input id={id} className="input" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} required />}
      </Field>
      <Field label="Your email">{(id) => <input id={id} className="input" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />}</Field>
      {error && (
        <div className="notice notice-error" role="alert">
          {error}
        </div>
      )}
      <button className="btn btn-primary btn-lg btn-block" disabled={busy}>
        {busy && <span className="spinner" />}
        Open project
      </button>
    </form>
  );
}

export function LoginPage() {
  useDocumentTitle('Sign in');
  const { user, loading, setSession } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [mode, setMode] = useState<Mode>('account');
  const next = params.get('next');
  const safeNext = next && next.startsWith('/') && !next.startsWith('//') ? next : '/';

  if (loading) return <PageLoading />;
  if (user) return <Navigate to={safeNext} replace />;

  return (
    <AuthFrame title="Sign in" subtitle="Open the work and leave your revisions right where they belong.">
      <div className="segmented" role="group" aria-label="Sign-in method">
        <button type="button" aria-pressed={mode === 'account'} onClick={() => setMode('account')}>
          My account
        </button>
        <button type="button" aria-pressed={mode === 'project'} onClick={() => setMode('project')}>
          Project password
        </button>
      </div>
      {mode === 'account' ? (
        <AccountForm
          onDone={(session) => {
            setSession(session);
            navigate(safeNext, { replace: true });
          }}
        />
      ) : (
        <ProjectForm
          project=""
          locked={false}
          onDone={(session) => {
            setSession(session);
            navigate(session.projectId ? `/projects/${session.projectId}` : '/', { replace: true });
          }}
        />
      )}
    </AuthFrame>
  );
}

/** Share link: /p/<project>. Sends signed-in members straight in; otherwise asks for the project password. */
export function ProjectEntryPage() {
  const { slug = '' } = useParams();
  const { user, loading, setSession } = useAuth();
  const navigate = useNavigate();
  const [gate, setGate] = useState<GateInfo | null>(null);
  const [checked, setChecked] = useState(false);
  useDocumentTitle(gate?.name || 'Open project');

  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    (async () => {
      if (user) {
        try {
          const found = await api.get<{ project: Project }>(`/api/projects/${encodeURIComponent(slug)}`);
          if (!cancelled) navigate(`/projects/${found.project.id}`, { replace: true });
          return;
        } catch (err) {
          if (!(err instanceof ApiError) || (err.status !== 404 && err.status !== 401)) throw err;
        }
      }
      const info = await api.get<GateInfo>(`/api/public/projects/${encodeURIComponent(slug)}`).catch(() => ({ gateEnabled: false }) as GateInfo);
      if (!cancelled) {
        setGate(info);
        setChecked(true);
      }
    })().catch(() => {
      if (!cancelled) setChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, [loading, user, slug, navigate]);

  if (loading || !checked) return <PageLoading label="Opening project" />;

  if (!gate?.gateEnabled) {
    if (!user) return <Navigate to={`/login?next=${encodeURIComponent(`/p/${slug}`)}`} replace />;
    return (
      <AuthFrame title="You do not have access to this project" subtitle="Ask your MK-Way contact to add you, or sign in with a different account.">
        <button className="btn btn-secondary" onClick={() => navigate('/')}>
          Back to my projects
        </button>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame title={gate.name || 'Project review'} subtitle={gate.clientName ? `${gate.clientName} · enter the project password to review and leave revisions.` : 'Enter the project password to review and leave revisions.'}>
      <ProjectForm
        project={gate.username || slug}
        locked
        onDone={(session) => {
          setSession(session);
          navigate(session.projectId ? `/projects/${session.projectId}` : '/', { replace: true });
        }}
      />
      <p className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
        Have your own account?{' '}
        <a href={`/login?next=${encodeURIComponent(`/p/${slug}`)}`} onClick={(e) => (e.preventDefault(), navigate(`/login?next=${encodeURIComponent(`/p/${slug}`)}`))}>
          Sign in instead
        </a>
      </p>
    </AuthFrame>
  );
}

/** Links shared from the previous version of Revisions: /project/<token> or /?token=<token>. */
export function LegacyLinkPage({ token: fromQuery }: { token?: string }) {
  const params = useParams();
  const navigate = useNavigate();
  const token = fromQuery || params.token || '';
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    api
      .get<{ slug: string }>(`/api/public/legacy/${encodeURIComponent(token)}`)
      .then((data) => navigate(`/p/${data.slug}`, { replace: true }))
      .catch(() => setMissing(true));
  }, [token, navigate]);

  if (!missing) return <PageLoading label="Opening project" />;
  return (
    <AuthFrame title="This link is no longer valid" subtitle="The project may have been removed. Ask your MK-Way contact for a new link.">
      <button className="btn btn-secondary" onClick={() => navigate('/login')}>
        Go to sign in
      </button>
    </AuthFrame>
  );
}

/** One-time link to choose a password (invite or reset). */
export function InvitePage() {
  useDocumentTitle('Set your password');
  const { token = '' } = useParams();
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [info, setInfo] = useState<{ email: string; name: string; purpose: string } | null>(null);
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<{ email: string; name: string; purpose: string }>(`/api/auth/invite/${encodeURIComponent(token)}`)
      .then((data) => {
        setInfo(data);
        setName(data.name);
      })
      .catch((err) => setInvalid(errorMessage(err)));
  }, [token]);

  if (invalid) {
    return (
      <AuthFrame title="This link no longer works" subtitle={invalid}>
        <button className="btn btn-secondary" onClick={() => navigate('/login')}>
          Go to sign in
        </button>
      </AuthFrame>
    );
  }
  if (!info) return <PageLoading />;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      setSession(await api.post<Session>('/api/auth/accept-invite', { token, password, name }));
      navigate('/', { replace: true });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthFrame title={info.purpose === 'reset' ? 'Choose a new password' : 'Welcome to Revisions'} subtitle={`Signing in as ${info.email}`}>
      <form className="auth-form" onSubmit={submit}>
        <Field label="Your name">{(id) => <input id={id} className="input" value={name} onChange={(e) => setName(e.target.value)} required />}</Field>
        <Field label="Password" hint="At least 8 characters.">
          {(id) => <input id={id} className="input" type="password" autoComplete="new-password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} required autoFocus />}
        </Field>
        {error && (
          <div className="notice notice-error" role="alert">
            {error}
          </div>
        )}
        <button className="btn btn-primary btn-lg btn-block" disabled={busy}>
          {busy && <span className="spinner" />}
          Save and continue
        </button>
      </form>
    </AuthFrame>
  );
}
